import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { AxiosInstance } from "axios";
import { IUser } from "../interfaces/IUser";
import { UserService } from "../services/userService";
import { RootState } from "../redux/store";

export const selectLoading = (state: RootState) => state.users.loading;
export const selectUsers = (state: RootState) => state.users.users;
export const selectUsersByType = (role: number) => createSelector(selectUsers, (users) => users.filter(user => user.role === role));
export const selectUserById = (userId: string) => createSelector(
    selectUsers,
    users => users.find(user => user.id === userId)
);

// Define async actions accepting `apiClient`
export const fetchUsers = createAsyncThunk<IUser[], AxiosInstance, { state: RootState }>(
    "users/fetch",
    async (apiClient, { getState }) => {
        const { users, lastFetched } = getState().users;

        // Fetch only if users are missing or outdated (older than 5 minutes)
        if (users.length > 0 && Date.now() - lastFetched < 5 * 60 * 1000) {
            return users;
        }

        // Fetch fresh data
        const userService = new UserService(apiClient);
        return await userService.getAll<IUser[]>();

    });

export const addUser = createAsyncThunk<IUser, { user: IUser; apiClient: AxiosInstance }>(
    "users/add",
    async ({ user, apiClient }) => {
        const userService = new UserService(apiClient);
        return await userService.create(user);
    }
);


export const updateUser = createAsyncThunk<IUser, { userId: string; updatedUser: IUser; apiClient: AxiosInstance }>(
    "users/update",
    async ({ userId, updatedUser, apiClient }) => {
        const userService = new UserService(apiClient);
        return await userService.update(userId, updatedUser);
    }
);

export const deleteUser = createAsyncThunk<string, { userId: string; apiClient: AxiosInstance }>(
    "users/delete",
    async ({ userId, apiClient }) => {
        const userService = new UserService(apiClient);
        await userService.delete(userId);
        return userId;
    }
);

// Create slice
const usersSlice = createSlice({
    name: "users",
    initialState: { users: [] as IUser[], loading: false, lastFetched: 0 },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchUsers.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchUsers.fulfilled, (state, action) => {
                state.loading = false;
                state.users = action.payload;
                state.lastFetched = Date.now();
            })
            .addCase(addUser.fulfilled, (state, action) => {
                state.users.push(action.payload);
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                state.users = state.users.map(user =>
                    user.id === action.payload.id ? action.payload : user
                );
            })
            .addCase(deleteUser.fulfilled, (state, action) => {
                state.users = state.users.filter(user => user.id !== action.payload);
            })
           
    }
});

export default usersSlice.reducer;